import FooterDesktop from "@Components/FooterV2/FooterDesktop";
import HeaderDesktop from "@Components/Header/HeaderDesktop";
import LazyLoadOnScroll from "@Components/LazyLoadOnScroll";
import LeadFormModalWithWrapper from "@Components/LeadFormModal";
import SectionRegionInfo from "@Pages/Search/components/SectionRegionInfo";
import { AppState } from "@Store/index";
import { PAGE } from "constants/configConstants";
import { lazy, memo, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import LeftSection from "../components/LeftSectionDesktop";
import MapLocationV2 from "../components/MapSectionV2";
import MapSectionChina from "../components/MapSectionV2/MapSectionChina";
import { LocationProvider } from "../components/MapSectionV2/locationContext";
import RightSection from "../components/RightSectionDesktop";
import SectionSchema from "../components/SectionSchema";
import ZendeskChat from "../components/ZendeskChat";
import config from "@Config/index";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import SectionNavigation from "@Components/SectionNavigation";
import { desktopNavigationData, navigationDataMapsV2 } from "../components/PropertyHeader/consants";
import FullPageLoader from "@Components/FullPageLoader";
import loadable from "@loadable/component";
import isAvailable from "@Utils/isAvailable";
// import VirtualTourConnectSection from "../components/VirtualTourConnectSection";
import NearbyProperties from "@Pages/Search/components/SimilarProperties";
import { getUpdatedNavigationData } from "../data/helper";
import { allVideoElementWithoutYoutube } from "../components/SectionFeatured.tsx/utils";
import styles from "./PropertyPageDesktop.module.scss";
import PropertyCTAs from "../components/PropertyCTAs";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import BudgetFriendlySection from "@Pages/Search/components/BudgetFriendlySection/BudgetFriendlySection";
import PopularProperties from "@Pages/Search/components/PopularPropertiesSection/PopularPropertiesSection";
import useTranslationHook from "hooks/useTranslationHook";
import AmberCopilotWrapper from "@Components/Chatbot/AmberCopilotWrapper";
import PriceTrend from "@Pages/PropertyPage/components/PriceTrends/PriceTrend";
import ReviewSectionWrapper from "../components/ReviewsSection/ReviewSectionWrapper";
import { MEET_THE_RESIDENT_COUNT } from "constants/index";
import SectionFeaturedDesktop from "../components/SectionFeaturedDesktop";
import PropertyTopSection from "../components/PropertyTopSection";
import RoomTypeContextProvider from "../components/RoomTypeSection/context";
import useOverviewGalleryArm from "../components/OverviewGallery/hooks/useOverviewGalleryArm";
import OverviewFoldContext from "../components/OverviewGallery/overviewFoldContext";
import overviewLayout from "../components/OverviewGallery/overviewLayout.module.scss";
import { trackOverviewGalleryExposure } from "../components/OverviewGallery/utils/galleryEvents";

const RecentelyViewedSection = lazy(
  () => import("@Pages/Search/components/RecentelyViewedSection/RecentelyViewedSection"),
);
// const ConnectSection = lazy(() => import("@Pages/PropertyPage/components/ConnectSection"));
const SimilarProperties = lazy(() => import("@Pages/Search/components/SimilarProperties"));

const FooterDesktopWhiteLabel = loadable(
  () => import("@Components/FooterWhiteLabel/FooterDesktopWhiteLabel"),
  {
    fallback: <FullPageLoader height="284px" />,
  },
);

const PropertyPageDesktop = (props: { isLoading: boolean }) => {
  const { isLoading } = props;
  const {
    isMapViewEnabled = false,
    inventoryData,
    installmentPlans,
    features,
    isInternal,
  } = useSelector(
    ({ propertyPageData, appData }: AppState) => ({
      inventoryData: propertyPageData.data,
      isMapViewEnabled: propertyPageData.data?.is_map_view_enabled,
      installmentPlans: propertyPageData?.installment_plans,
      features: propertyPageData?.data?.features || [],
      isInternal: appData.isInternalUser,
    }),
    shallowEqual,
  );

  const { isWhitelabel } = useWhitelabel();
  // For the new variant the server and the first client render hold both this full-width fold
  // and LeftSection's old one; CSS shows this one at ≥1200 (overviewLayout.module.scss), and
  // after mount the hidden one is dropped.
  const { variant: overviewVariant, mode, arm: overviewGalleryArm } = useOverviewGalleryArm();
  const isOverviewTopFold = overviewVariant === "new" && mode !== "old" && !isLoading;
  useEffect(() => {
    if (overviewGalleryArm) trackOverviewGalleryExposure(overviewGalleryArm);
  }, [overviewGalleryArm]);
  // The section tabs live in the left column, which now starts below the full-width top fold.
  // Hide them while the top fold is on screen, so they only show once they can stick under the
  // header, where they sat before. While the fold is display:none it never intersects.
  const topFoldRef = useRef<HTMLDivElement>(null);
  const [isTopFoldOnScreen, setIsTopFoldOnScreen] = useState(false);
  useEffect(() => {
    const element = topFoldRef.current;
    if (!isOverviewTopFold || !element) {
      setIsTopFoldOnScreen(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsTopFoldOnScreen(entry.isIntersecting),
      // Fires when the top fold's bottom reaches the tabs' sticky position (the header height
      // less the gap between the fold and the tabs).
      { rootMargin: "-46px 0px 0px 0px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [isOverviewTopFold]);
  const { t } = useTranslationHook();
  const isAmenitiesBeforeRoomTypes = true; // concluded experiment
  const isMeetTheResidentsEnabled = true;

  const navigationVal = useMemo(() => {
    const hasBillsIncluded =
      features &&
      features.some((amenity: any) => ["Bills Included"].includes(amenity.name_en || amenity.name));

    const hasInstallmentPlans = installmentPlans === null ? true : installmentPlans?.length > 0;

    // check to show Payment and Fees
    const hasPaymentPolicies = inventoryData?.description?.some(
      (item: any) => item.name === "payment",
    );
    const hasPaymentAndFeesData = hasInstallmentPlans || hasBillsIncluded || hasPaymentPolicies;

    const hasMeetTheResidentsData =
      (__CLIENT__ ? isMeetTheResidentsEnabled : true) &&
      Boolean(
        inventoryData.meta?.property_residents_insights?.visible &&
          inventoryData.meta?.property_residents_insights?.uni_dist?.length >=
            MEET_THE_RESIDENT_COUNT,
      );

    const baseNavigationData =
      isMapViewEnabled || config.IS_CHINA_APP ? navigationDataMapsV2(t) : desktopNavigationData(t);

    const reorderedNavigationData = [...baseNavigationData];
    if (isAmenitiesBeforeRoomTypes) {
      const amenitiesIndex = reorderedNavigationData.findIndex((item) => item.id === "amenities");
      const roomTypeIndex = reorderedNavigationData.findIndex((item) => item.id === "room_type");
      if (amenitiesIndex !== -1 && roomTypeIndex !== -1 && amenitiesIndex > roomTypeIndex) {
        const [amenitiesItem] = reorderedNavigationData.splice(amenitiesIndex, 1);
        reorderedNavigationData.splice(roomTypeIndex, 0, amenitiesItem);
      }
    }

    const navigationData = getUpdatedNavigationData({
      navigationData: reorderedNavigationData,
      array: [
        {
          id: "payment_and_fees",
          condition: hasPaymentAndFeesData,
        },
        {
          id: "video_tour",
          condition: inventoryData
            ? allVideoElementWithoutYoutube(inventoryData).length >= 3
            : false,
        },
        {
          id: "meet_the_residents",
          condition: hasMeetTheResidentsData,
        },
      ],
    });

    return navigationData;
  }, [
    installmentPlans,
    inventoryData,
    features,
    isAmenitiesBeforeRoomTypes,
    isMeetTheResidentsEnabled,
  ]);

  const navigationTabList: any = !isAvailable(inventoryData)
    ? (() => {
        const arr = [...desktopNavigationData(t)];
        const idx = arr.findIndex((item) => item.id === "similar_properties");
        if (idx > -1) {
          const [similar] = arr.splice(idx, 1);
          arr.splice(1, 0, similar);
        }

        if (isAmenitiesBeforeRoomTypes) {
          const amenitiesIndex = arr.findIndex((item) => item.id === "amenities");
          const roomTypeIndex = arr.findIndex((item) => item.id === "room_type");
          if (amenitiesIndex !== -1 && roomTypeIndex !== -1 && amenitiesIndex > roomTypeIndex) {
            const [amenitiesItem] = arr.splice(amenitiesIndex, 1);
            arr.splice(roomTypeIndex, 0, amenitiesItem);
          }
        }

        return getUpdatedNavigationData({
          navigationData: arr,
          array: [
            {
              id: "meet_the_residents",
              condition: false,
            },
          ],
        });
      })()
    : navigationVal;

  const renderTopLoadingSections = () => (
    <>
      {!isLoading && <SectionSchema />}
      <HeaderDesktop parentPage={PAGE.INVENTORY} />
    </>
  );

  const rendeProprtySections = () => (
    <>
      <div
        className={formatClassNames(
          isWhitelabel
            ? styles.propertyPageWhiteLabelPaddingConfig
            : styles.propertyPagePaddingConfig,
          isOverviewTopFold && styles.overviewTopPadding,
        )}
      >
        <SectionNavigation
          gtagEvent="clicked_tabs"
          globalTopOffset={62}
          navigationData={navigationTabList}
          addSticky
          isLazyElementsPrecent
          zIndex={12}
          isMapViewEnabled={isMapViewEnabled}
          largeElement
        />
        {isOverviewTopFold && (
          // PropertyTopSection mounts the gallery viewer (GalleryV2), which reads
          // useRoomTypeContext(); outside a provider that context is {} and GalleryV2 crashes. The
          // provider dedupes its room-types fetch (activeFetchKey), so a second one here is safe.
          <RoomTypeContextProvider isLoading={isLoading}>
            <OverviewFoldContext.Provider value>
              <div
                className={formatClassNames(styles.overviewTopFold, overviewLayout.newOnly)}
                ref={topFoldRef}
              >
                <SectionFeaturedDesktop
                  isLoading={false}
                  isBookFormEnabledForInactiveProperty={false}
                  isOverviewFold
                />
                <PropertyTopSection isDesktop />
              </div>
            </OverviewFoldContext.Provider>
          </RoomTypeContextProvider>
        )}
        <div className={styles.propertyPageGridContainer}>
          <div>
            {!isLoading && (
              <SectionNavigation
                gtagEvent="clicked_tabs"
                globalTopOffset={62}
                navigationData={navigationTabList}
                addSticky
                isLazyElementsPrecent
                wrapperClassName={formatClassNames(
                  isOverviewTopFold && styles.overviewNav,
                  isOverviewTopFold && isTopFoldOnScreen && styles.overviewNavHidden,
                )}
              />
            )}
            <LeftSection
              isLoading={isLoading}
              isAgentView={isInternal}
              isMapSectionV2={isMapViewEnabled || config.IS_CHINA_APP}
            />
          </div>
          <div className={styles.propertyPageBottomCTA}>
            <PropertyCTAs />
          </div>
          <RightSection inventoryMeta={inventoryData?.meta} />
        </div>
        {!isLoading && (
          <div className={styles.propertyPageBottomSections}>
            {isMapViewEnabled || config.IS_CHINA_APP ? (
              <>
                <LocationProvider>
                  {config.IS_CHINA_APP || config.SHOW_MAPBOX_GLOBAL ? (
                    <MapSectionChina isDesktop />
                  ) : (
                    <MapLocationV2 isDesktop />
                  )}
                </LocationProvider>
                {/* <VirtualTourConnectSection /> */}
              </>
            ) : (
              <></>
            )}
            <ReviewSectionWrapper isDesktop />
            <PriceTrend isDesktop />
            {/* <LazyLoadOnScroll
              fallback={<section style={{ height: "400px" }} id="connectSection" />}
            >
              <ConnectSection isDesktop />
            </LazyLoadOnScroll> */}

            <LazyLoadOnScroll
              fallback={<section style={{ height: "340px" }} id="recently-viewed" />}
            >
              <RecentelyViewedSection gtagEventCategory={PAGE.INVENTORY} isDesktop />
            </LazyLoadOnScroll>
            <LazyLoadOnScroll
              fallback={<section style={{ height: "340px" }} id="similar_properties" />}
            >
              <SimilarProperties />
            </LazyLoadOnScroll>
            <NearbyProperties isNearbyProperties />
            <LazyLoadOnScroll
              fallback={<section style={{ height: "340px" }} id="popular_properties" />}
            >
              <PopularProperties gtagEventCategory={PAGE.INVENTORY} isDesktop />
            </LazyLoadOnScroll>
            <BudgetFriendlySection gtagEventCategory={PAGE.INVENTORY} isDesktop />
            <SectionRegionInfo includeFields={["description"]} isDesktop isPropertyPage />
            <SectionRegionInfo includeFields={["links"]} isDesktop isPropertyPage />
          </div>
        )}
      </div>

      {isWhitelabel ? <FooterDesktopWhiteLabel /> : <FooterDesktop />}
      <LeadFormModalWithWrapper isPropertyPage />
      {!isWhitelabel && <ZendeskChat />}
      <AmberCopilotWrapper />
    </>
  );

  return (
    <>
      {renderTopLoadingSections()}
      {rendeProprtySections()}
    </>
  );
};
export default wrapperHOC(memo(PropertyPageDesktop), {
  componentName: "PropertyPageDesktop",
  showForChina: true,
});
